import { useCallback } from "react";
import type {
  EventTransformerHooks,
  UseInstantTransformEvent,
} from "@b2bportal/core-tracking";
import {
  ActivityCommonTrackingEventsName,
  ACTIVITY_PRODUCT_TYPE,
  type ActivitiesAppliedFilter,
  type ActivityHTSEvent,
  type ViewedActivitiesSearch,
  type ViewedActivityDetails,
  type ViewedActivityInList,
} from "./events";
import {
  ActivitiesRawEventName,
  type ActivitiesRawEvent,
  type ActivityDetailsViewedRawEvent,
  type ActivityFiltersAppliedRawEvent,
  type ActivitySearchLoadedRawEvent,
  type ActivityViewedInListRawEvent,
} from "./types";

const commonProperties = {
  productType: ACTIVITY_PRODUCT_TYPE,
};

export const useActivityViewedInListTransformer: UseInstantTransformEvent<
  ActivityViewedInListRawEvent,
  ViewedActivityInList
> = (name) => {
  return useCallback((event) => {
    return {
      name: ActivityCommonTrackingEventsName.viewed_activity_in_list,
      properties: {
        ...commonProperties,
        productId: event.properties.productId,
        price: event.properties.price,
        sort_index: event.properties.sortIndex,
        sort_used: event.properties.sortUsed,
        activity_name: event.properties.activity_name,
      },
    };
  }, []);
};

export const useActivityDetailsViewedTransformer: UseInstantTransformEvent<
  ActivityDetailsViewedRawEvent,
  ViewedActivityDetails
> = (name) => {
  return useCallback((event) => {
    return {
      name: ActivityCommonTrackingEventsName.viewed_activity_details,
      properties: {
        ...commonProperties,
        productId: event.properties.productId,
        activity_name: event.properties.activity_name,
        avg_rating: event.properties.avg_rating,
        number_of_reviews: event.properties.number_of_reviews,
        number_of_photos: event.properties.number_of_photos,
        total_price: event.properties.total_price,
      },
    };
  }, []);
};

export const useActivityFiltersAppliedTransformer: UseInstantTransformEvent<
  ActivityFiltersAppliedRawEvent,
  ActivitiesAppliedFilter
> = (name) => {
  return useCallback((event) => {
    return {
      name: ActivityCommonTrackingEventsName.activities_applied_filter,
      properties: {
        ...commonProperties,
        filter_type: event.properties.filterTypes.join(", "),
        filters_applied: event.properties.filterTypes,
        location_searched: event.properties.location,
        categoriesCount: event.properties.categoriesCount,
        priceFilterApplied: event.properties.priceFilterApplied,
        ratingFilterApplied: event.properties.ratingFilterApplied,
        timeFilterApplied: event.properties.timeFilterApplied,
        durationFilterApplied: event.properties.durationFilterApplied,
        tagsFilterApplied: event.properties.tagsFilterApplied,
      },
    };
  }, []);
};

export const useActivitySearchLoadedTransformer: UseInstantTransformEvent<
  ActivitySearchLoadedRawEvent,
  ViewedActivitiesSearch
> = () => {
  return useCallback(() => {
    return {
      name: ActivityCommonTrackingEventsName.viewed_activities_search,
      properties: {
        ...commonProperties,
      },
    };
  }, []);
};

export const ACTIVITIES_HTS_EVENT_TRANSFORMER_HOOKS: EventTransformerHooks<
  ActivitiesRawEvent,
  ActivityHTSEvent
> = {
  [ActivitiesRawEventName.ActivityViewedInList]: [
    useActivityViewedInListTransformer,
  ],
  [ActivitiesRawEventName.ActivityDetailsViewed]: [
    useActivityDetailsViewedTransformer,
  ],
  [ActivitiesRawEventName.ActivityFiltersApplied]: [
    useActivityFiltersAppliedTransformer,
  ],
  [ActivitiesRawEventName.ActivitySearchLoaded]: [
    useActivitySearchLoadedTransformer,
  ],
};
